import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiButtonEmpty,
  EuiButtonGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiBasicTableColumn,
  EuiInMemoryTable,
  EuiLink,
  EuiEmptyPrompt,
  EuiCallOut,
  EuiHorizontalRule,
  EuiAvatar,
  EuiToolTip,
  EuiIcon,
  EuiCode,
  EuiTextColor,
  EuiFieldSearch,
  EuiHealth,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../../can";
import {
  NewTicketForm,
  TicketAuditTrail,
  TicketReplyForm,
} from "./otobo-ticket-actions";
import {
  OtoboArticle,
  OtoboInstanceTickets,
  OtoboTicket,
} from "src/questionnaires";

type TranslateFn = ReturnType<typeof useAnketaI18n>["translate"];

type View = "list" | "detail";
type StateFilter = "all" | "open" | "closed";

/** OTOBO timestamps are `YYYY-MM-DD HH:mm:ss` (system tz); make them Date-parseable. */
const parseOtoboDate = (s: string): Date =>
  new Date((s ?? "").replace(" ", "T"));

function relativeTime(dateString: string, translate: TranslateFn): string {
  const d = parseOtoboDate(dateString);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (!Number.isFinite(diff)) return dateString ?? "-";
  if (diff < 60)
    return translate("anketa.opsanio.appliance.flyout.otobo.time.justNow", {
      defaultMessage: "Just now",
    });
  if (diff < 3600)
    return translate("anketa.opsanio.appliance.flyout.otobo.time.minutesAgo", {
      values: { count: Math.floor(diff / 60) },
      defaultMessage: "{{count}} min ago",
    });
  if (diff < 86400)
    return translate("anketa.opsanio.appliance.flyout.otobo.time.hoursAgo", {
      values: { count: Math.floor(diff / 3600) },
      defaultMessage: "{{count}} h ago",
    });
  if (diff < 2592000)
    return translate("anketa.opsanio.appliance.flyout.otobo.time.daysAgo", {
      values: { count: Math.floor(diff / 86400) },
      defaultMessage: "{{count}} days ago",
    });
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const StateBadge = ({
  ticket,
  translate,
}: {
  ticket: OtoboTicket;
  translate: TranslateFn;
}) => (
  <EuiHealth
    color={ticket.category === "open" ? "success" : "subdued"}
    style={{ fontSize: 12 }}
  >
    {ticket.State ||
      (ticket.category === "open"
        ? translate("anketa.opsanio.appliance.flyout.otobo.state.open", {
            defaultMessage: "Open",
          })
        : translate("anketa.opsanio.appliance.flyout.otobo.state.closed", {
            defaultMessage: "Closed",
          }))}
  </EuiHealth>
);

const PRIORITY_COLORS: Record<string, string> = {
  "1 very low": "subdued",
  "2 low": "subdued",
  "3 normal": "default",
  "4 high": "warning",
  "5 very high": "danger",
};

const ArticleCard = ({
  article,
  formatDate,
  translate,
}: {
  article: OtoboArticle;
  formatDate: (d: string) => string;
  translate: TranslateFn;
}) => (
  <EuiPanel
    paddingSize="m"
    hasShadow={false}
    hasBorder
    style={{ marginBottom: 8 }}
  >
    <EuiFlexGroup alignItems="flexStart" gutterSize="m" responsive={false}>
      <EuiFlexItem grow={false}>
        <EuiAvatar
          name={article.From || article.SenderType || "?"}
          size="m"
          color={article.SenderType === "customer" ? "#FCE5DB" : undefined}
        />
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiFlexGroup
          justifyContent="spaceBetween"
          alignItems="center"
          gutterSize="s"
          responsive={false}
        >
          <EuiFlexItem grow={false}>
            <EuiText size="s">
              <strong>
                {article.From ||
                  article.SenderType ||
                  translate(
                    "anketa.opsanio.appliance.flyout.otobo.article.unknownSender",
                    { defaultMessage: "Unknown" },
                  )}
              </strong>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiFlexGroup
              gutterSize="xs"
              alignItems="center"
              responsive={false}
            >
              {article.SenderType && (
                <EuiFlexItem grow={false}>
                  <EuiBadge color="hollow" style={{ fontSize: 10 }}>
                    {article.SenderType}
                  </EuiBadge>
                </EuiFlexItem>
              )}
              <EuiFlexItem grow={false}>
                <EuiToolTip content={formatDate(article.CreateTime)}>
                  <EuiTextColor color="subdued">
                    <EuiText size="xs">
                      {relativeTime(article.CreateTime, translate)}
                    </EuiText>
                  </EuiTextColor>
                </EuiToolTip>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
        {article.Subject && (
          <>
            <EuiSpacer size="xs" />
            <EuiText size="s">
              <strong>{article.Subject}</strong>
            </EuiText>
          </>
        )}
        <EuiSpacer size="xs" />
        <EuiText size="s" style={{ whiteSpace: "pre-wrap" }}>
          {article.Body}
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  </EuiPanel>
);

const TicketDetailView = ({
  ticket,
  loadingDetail,
  formatDate,
  onBack,
  translate,
  canReply,
  onUpdated,
}: {
  ticket: OtoboTicket;
  loadingDetail: boolean;
  formatDate: (d: string) => string;
  onBack: () => void;
  translate: TranslateFn;
  /** opsanio/otobo · create: may append a reply / change state. */
  canReply: boolean;
  onUpdated: (ticket: OtoboTicket) => void;
}) => {
  const articles = ticket.Article ?? [];
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <EuiButtonEmpty
        iconType="arrowLeft"
        size="s"
        onClick={onBack}
        style={{ alignSelf: "flex-start", marginBottom: 8 }}
      >
        {translate("anketa.opsanio.appliance.flyout.otobo.detail.back", {
          defaultMessage: "Back to overview",
        })}
      </EuiButtonEmpty>

      <EuiPanel paddingSize="m" hasShadow={false} color="subdued" hasBorder>
        <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <StateBadge ticket={ticket} translate={translate} />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiCode style={{ fontSize: 12 }}>#{ticket.TicketNumber}</EuiCode>
          </EuiFlexItem>
          {ticket.Priority && (
            <EuiFlexItem grow={false}>
              <EuiBadge color={PRIORITY_COLORS[ticket.Priority] ?? "hollow"}>
                {ticket.Priority}
              </EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
        <EuiSpacer size="xs" />
        <EuiTitle size="s">
          <h3>
            {ticket.web_url ? (
              <EuiLink target="_blank" href={ticket.web_url} external>
                {ticket.Title}
              </EuiLink>
            ) : (
              ticket.Title
            )}
          </h3>
        </EuiTitle>
        <EuiSpacer size="xs" />
        <EuiFlexGroup
          gutterSize="m"
          alignItems="center"
          responsive={false}
          wrap
        >
          {ticket.Owner && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                <EuiIcon type="user" size="s" /> {ticket.Owner}
              </EuiText>
            </EuiFlexItem>
          )}
          {ticket.CustomerUserID && (
            <EuiFlexItem grow={false}>
              <EuiText size="xs" color="subdued">
                <EuiIcon type="email" size="s" /> {ticket.CustomerUserID}
              </EuiText>
            </EuiFlexItem>
          )}
          <EuiFlexItem grow={false}>
            <EuiToolTip content={formatDate(ticket.Created)}>
              <EuiText size="xs" color="subdued">
                <EuiIcon type="clock" size="s" />{" "}
                {translate(
                  "anketa.opsanio.appliance.flyout.otobo.detail.createdLabel",
                  { defaultMessage: "Created" },
                )}{" "}
                {relativeTime(ticket.Created, translate)}
              </EuiText>
            </EuiToolTip>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiToolTip content={formatDate(ticket.Changed)}>
              <EuiText size="xs" color="subdued">
                <EuiIcon type="refresh" size="s" />{" "}
                {translate(
                  "anketa.opsanio.appliance.flyout.otobo.detail.updatedLabel",
                  { defaultMessage: "Updated" },
                )}{" "}
                {relativeTime(ticket.Changed, translate)}
              </EuiText>
            </EuiToolTip>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiPanel>

      <EuiSpacer size="m" />

      <EuiFlexGroup
        gutterSize="s"
        alignItems="center"
        responsive={false}
        style={{ marginBottom: 8 }}
      >
        <EuiFlexItem grow={false}>
          <EuiText
            size="xs"
            color="subdued"
            style={{
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: 600,
            }}
          >
            {translate("anketa.opsanio.appliance.flyout.otobo.detail.history", {
              defaultMessage: "History",
            })}
          </EuiText>
        </EuiFlexItem>
        {!loadingDetail && (
          <EuiFlexItem grow={false}>
            <EuiBadge color="hollow">{articles.length}</EuiBadge>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>

      {loadingDetail ? (
        <EuiFlexGroup justifyContent="center" style={{ padding: "24px 0" }}>
          <EuiLoadingSpinner size="l" />
        </EuiFlexGroup>
      ) : articles.length === 0 ? (
        <EuiText size="s" color="subdued">
          <em>
            {translate(
              "anketa.opsanio.appliance.flyout.otobo.detail.noArticles",
              { defaultMessage: "No articles available." },
            )}
          </em>
        </EuiText>
      ) : (
        articles.map((article) => (
          <ArticleCard
            key={article.ArticleID}
            article={article}
            formatDate={formatDate}
            translate={translate}
          />
        ))
      )}

      <EuiSpacer size="m" />
      <TicketAuditTrail ticketId={ticket.TicketID} formatDate={formatDate} />

      {canReply && !loadingDetail && (
        <>
          <EuiSpacer size="m" />
          <TicketReplyForm ticket={ticket} onUpdated={onUpdated} />
        </>
      )}
    </div>
  );
};

export const OpsanioApplianceFlyoutOtobo = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  // The setup request: appliance · create AND otobo · create (internal).
  const can = useOpsanioCan();
  const canRequestSetup =
    can(AnketaResourcesEnum.opsanioAppliance, "create") &&
    can(AnketaResourcesEnum.opsanioOtobo, "create", { isInternal: true });
  // Replying writes to OTOBO: opsanio/otobo · create (internal), as on the server.
  const canReply = can(AnketaResourcesEnum.opsanioOtobo, "create", { isInternal: true });
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();

  const [tickets, setTickets] = useState<OtoboTicket[]>([]);
  const [queue, setQueue] = useState<string>("");
  const [queueMissing, setQueueMissing] = useState<boolean>(false);
  const [requesting, setRequesting] = useState<boolean>(false);
  const [creatingQueue, setCreatingQueue] = useState<boolean>(false);
  const [showNewTicket, setShowNewTicket] = useState<boolean>(false);
  const [requested, setRequested] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [unavailableMsg, setUnavailableMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<OtoboTicket | null>(null);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [view, setView] = useState<View>("list");

  const [searchText, setSearchText] = useState("");
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");

  const i18nPrefix = "anketa.opsanio.appliance.otobo.ticket";
  const instanceName = appliance.common?.metadata?.name ?? "";

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && instanceName.trim() !== "",
    [keycloak, instanceName],
  );

  const formatDate = useCallback(
    (dateString: string) =>
      parseOtoboDate(dateString).toLocaleString("de-DE", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const getTickets = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    setUnavailableMsg(null);
    setQueueMissing(false);
    setRequested(false);
    setSelected(null);
    setView("list");

    try {
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/otobo/instance/${encodeURIComponent(
          instanceName,
        )}`,
        responseType: "json",
        token: keycloak.token,
      });

      if (error) {
        // 503 -> OTOBO not configured / search operation not exposed; render an
        // "unavailable" empty state with the backend's specific message.
        if ((error as any).status === 503) {
          setUnavailable(true);
          setUnavailableMsg((error as any)?.service?.message ?? null);
        } else {
          // The backend says why (e.g. the MCP server's answer); axios only
          // knows "Request failed with status code 502".
          const reason = (error as any)?.service?.message ?? error.message;
          setError(reason);
          addToast({
            title: translate(`${i18nPrefix}.fetch.error`),
            color: "danger",
            text: reason,
          });
        }
      } else {
        const payload = (data as any)?.data as OtoboInstanceTickets | undefined;
        setTickets(Array.isArray(payload?.tickets) ? payload!.tickets : []);
        setQueue(payload?.queue ?? "");
        setQueueMissing(payload?.queueExists === false);
      }
    } catch (err) {
      console.error(err);
      setError(
        translate("anketa.opsanio.appliance.flyout.otobo.error.unexpected", {
          defaultMessage: "Unexpected error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [canFetch, instanceName]);

  // No OTOBO API exists to create queues/PostMaster filters, so this raises a
  // human-actionable setup-request mail (queue name + mail-flow spec) instead.
  const requestQueueSetup = useCallback(async () => {
    if (!canFetch) return;
    setRequesting(true);
    try {
      const { success, error, data } = await Post({
        path: `v2/${AnketaResourcesEnum.opsanio}/otobo/instance/${encodeURIComponent(
          instanceName,
        )}`,
        value: { questionnaireId: appliance.id },
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || error) {
        addToast({
          title: translate(`${i18nPrefix}.request.error`),
          color: "danger",
          text:
            error?.message ??
            translate(
              "anketa.opsanio.appliance.flyout.otobo.request.error.text",
              { defaultMessage: "The request could not be sent." },
            ),
        });
      } else if ((data as any)?.data?.queueExists === true) {
        // The queue exists by now (checked via the OTOBO MCP server): nothing
        // was requested - show its tickets instead.
        addToast({
          title: translate(`${i18nPrefix}.request.exists`, {
            defaultMessage: "The queue already exists",
          }),
          color: "primary",
          text: queue,
        });
        void getTickets();
      } else {
        setRequested(true);
        addToast({
          title: translate(`${i18nPrefix}.request.success`),
          color: "success",
          text: translate(
            "anketa.opsanio.appliance.flyout.otobo.request.success.text",
            {
              values: { queue },
              defaultMessage: "Setup for {{queue}} requested.",
            },
          ),
        });
      }
    } catch (err) {
      console.error(err);
      addToast({
        title: translate(`${i18nPrefix}.request.error`),
        color: "danger",
        text: translate(
          "anketa.opsanio.appliance.flyout.otobo.request.error.text",
          { defaultMessage: "The request could not be sent." },
        ),
      });
    } finally {
      setRequesting(false);
    }
  }, [canFetch, instanceName, keycloak, Post, appliance, queue, getTickets]);

  const fetchTicketDetail = useCallback(
    async (ticket: OtoboTicket) => {
      if (!canFetch) return;
      setLoadingDetail(true);
      try {
        const { data, error } = await Get({
          path: `v2/${AnketaResourcesEnum.opsanio}/otobo/ticket/${ticket.TicketID}`,
          responseType: "json",
          token: keycloak.token,
        });
        if (error) {
          addToast({
            title: translate(`${i18nPrefix}.detail.fetch.error`),
            color: "danger",
            text: error.message,
          });
        } else {
          const full = (data as any)?.data as OtoboTicket | null;
          if (full) setSelected(full);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingDetail(false);
      }
    },
    [canFetch],
  );

  const handleSelect = useCallback(
    (ticket: OtoboTicket) => {
      setSelected(ticket);
      setView("detail");
      fetchTicketDetail(ticket);
    },
    [fetchTicketDetail],
  );

  const handleBack = useCallback(() => {
    setView("list");
    setSelected(null);
  }, []);

  useEffect(() => {
    getTickets();
  }, [appliance]);

  const filteredTickets = useMemo(() => {
    const needle = searchText.toLowerCase();
    return tickets.filter((t) => {
      if (stateFilter !== "all" && t.category !== stateFilter) return false;
      if (needle) {
        const haystack = [
          t.Title,
          t.TicketNumber,
          t.Owner,
          t.CustomerUserID,
          t.CustomerID,
          t.State,
          t.Priority,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [tickets, searchText, stateFilter]);

  const openCount = useMemo(
    () => tickets.filter((t) => t.category === "open").length,
    [tickets],
  );
  const closedCount = useMemo(
    () => tickets.filter((t) => t.category === "closed").length,
    [tickets],
  );

  const stateButtons = [
    {
      id: "all",
      label: translate("anketa.opsanio.appliance.flyout.otobo.filter.all", {
        defaultMessage: "All",
      }),
    },
    {
      id: "open",
      label: translate("anketa.opsanio.appliance.flyout.otobo.state.open", {
        defaultMessage: "Open",
      }),
    },
    {
      id: "closed",
      label: translate("anketa.opsanio.appliance.flyout.otobo.state.closed", {
        defaultMessage: "Closed",
      }),
    },
  ];

  const columns: Array<EuiBasicTableColumn<OtoboTicket>> = useMemo(
    () => [
      {
        field: "TicketNumber",
        name: translate(
          "anketa.opsanio.appliance.flyout.otobo.column.number",
          { defaultMessage: "Number" },
        ),
        width: "120px",
        sortable: true,
        render: (num: string, item: OtoboTicket) =>
          item.web_url ? (
            <EuiLink target="_blank" href={item.web_url}>
              <EuiCode style={{ fontSize: 12 }}>#{num}</EuiCode>
            </EuiLink>
          ) : (
            <EuiCode style={{ fontSize: 12 }}>#{num}</EuiCode>
          ),
      },
      {
        field: "Title",
        name: translate("anketa.opsanio.appliance.flyout.otobo.column.title", {
          defaultMessage: "Title",
        }),
        sortable: true,
        render: (title: string) => (
          <EuiText size="s" style={{ fontWeight: 500 }}>
            {title}
          </EuiText>
        ),
      },
      {
        field: "State",
        name: translate(
          "anketa.opsanio.appliance.flyout.otobo.column.status",
          { defaultMessage: "Status" },
        ),
        width: "150px",
        sortable: true,
        render: (_: string, item: OtoboTicket) => (
          <StateBadge ticket={item} translate={translate} />
        ),
      },
      {
        field: "Priority",
        name: translate(
          "anketa.opsanio.appliance.flyout.otobo.column.priority",
          { defaultMessage: "Priority" },
        ),
        width: "120px",
        sortable: true,
        render: (priority: string) =>
          priority ? (
            <EuiBadge color={PRIORITY_COLORS[priority] ?? "hollow"}>
              {priority}
            </EuiBadge>
          ) : (
            <EuiText size="xs" color="subdued">
              -
            </EuiText>
          ),
      },
      {
        field: "Owner",
        name: translate("anketa.opsanio.appliance.flyout.otobo.column.owner", {
          defaultMessage: "Owner",
        }),
        width: "140px",
        render: (owner: string) =>
          owner ? (
            <EuiFlexGroup
              gutterSize="xs"
              alignItems="center"
              responsive={false}
            >
              <EuiFlexItem grow={false}>
                <EuiAvatar name={owner} size="s" />
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiText size="xs">{owner}</EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          ) : (
            <EuiText size="xs" color="subdued">
              -
            </EuiText>
          ),
      },
      {
        field: "Changed",
        name: translate(
          "anketa.opsanio.appliance.flyout.otobo.column.changed",
          { defaultMessage: "Updated" },
        ),
        width: "130px",
        sortable: true,
        render: (date: string) => (
          <EuiToolTip content={formatDate(date)}>
            <EuiText size="xs" color="subdued">
              {relativeTime(date, translate)}
            </EuiText>
          </EuiToolTip>
        ),
      },
      {
        width: "48px",
        actions: [
          {
            name: translate("anketa.table.details"),
            description: translate("anketa.table.details"),
            icon: "expand",
            type: "icon",
            onClick: handleSelect,
          },
        ],
      },
    ],
    [handleSelect, formatDate, translate],
  );

  // Call-to-action that raises the "set up queue + mail-flow" request mail.
  // Shown both when the queue is missing and when the connector can't search,
  // since both are resolved by the same manual OTOBO setup.
  // Create the queue through the OTOBO MCP server, then show its (empty) list.
  const createQueue = async () => {
    if (!canFetch) return;
    setCreatingQueue(true);
    const { success, error } = await Post({
      path: `v2/${AnketaResourcesEnum.opsanio}/otobo/instance/${encodeURIComponent(
        instanceName,
      )}/queue`,
      value: {},
      responseType: "json",
      token: keycloak.token,
    });
    setCreatingQueue(false);
    if (!success || error) {
      addToast({
        title: translate("anketa.opsanio.appliance.flyout.otobo.queue.create.error", {
          defaultMessage: "The queue could not be created",
        }),
        color: "danger",
        text: (error as any)?.service?.message ?? error?.message,
      });
      return;
    }
    addToast({
      title: translate("anketa.opsanio.appliance.flyout.otobo.queue.create.success", {
        defaultMessage: "Queue created",
      }),
      color: "success",
      text: queue,
    });
    void getTickets();
  };

  const queueMissingActions = !canRequestSetup ? null : (
    <EuiFlexGroup gutterSize="s" justifyContent="center" responsive={false} wrap>
      <EuiFlexItem grow={false}>
        <EuiButton
          color="primary"
          fill
          iconType="plusInCircle"
          onClick={createQueue}
          isLoading={creatingQueue}
          disabled={creatingQueue}
        >
          {translate("anketa.opsanio.appliance.flyout.otobo.queue.create.button", {
            defaultMessage: "Create queue",
          })}
        </EuiButton>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        {requested ? (
          <EuiText size="s" color="success">
            <EuiIcon type="check" />{" "}
            {translate("anketa.opsanio.appliance.flyout.otobo.setup.requested", {
              defaultMessage: "Setup requested.",
            })}
          </EuiText>
        ) : (
          <EuiButton
            iconType="email"
            onClick={requestQueueSetup}
            isLoading={requesting}
            disabled={requesting || creatingQueue}
          >
            {translate("anketa.opsanio.appliance.flyout.otobo.setup.requestButton", {
              defaultMessage: "Request setup via email",
            })}
          </EuiButton>
        )}
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const setupCta = requested ? (
    <EuiText size="s" color="success">
      <EuiIcon type="check" />{" "}
      {translate("anketa.opsanio.appliance.flyout.otobo.setup.requested", {
        defaultMessage: "Setup requested.",
      })}
    </EuiText>
  ) : !canRequestSetup ? null : (
    <EuiButton
      color="primary"
      fill
      iconType="email"
      onClick={requestQueueSetup}
      isLoading={requesting}
      disabled={requesting}
    >
      {translate("anketa.opsanio.appliance.flyout.otobo.setup.requestButton", {
        defaultMessage: "Request setup via email",
      })}
    </EuiButton>
  );

  return (
    <EuiPanel
      paddingSize="m"
      hasShadow={false}
      hasBorder={false}
      color="transparent"
    >
      <EuiFlexGroup
        justifyContent="spaceBetween"
        alignItems="center"
        gutterSize="s"
        responsive={false}
      >
        <EuiFlexItem>
          <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type="email" size="l" />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiTitle size="s">
                <h3 style={{ margin: 0 }}>
                  {translate(
                    "anketa.opsanio.appliance.flyout.otobo.heading",
                    { defaultMessage: "OTOBO Tickets" },
                  )}
                </h3>
              </EuiTitle>
            </EuiFlexItem>
            {!loading && tickets.length > 0 && (
              <>
                <EuiFlexItem grow={false}>
                  <EuiBadge color="success">
                    {translate(
                      "anketa.opsanio.appliance.flyout.otobo.badge.openCount",
                      {
                        values: { count: openCount },
                        defaultMessage: "{{count}} open",
                      },
                    )}
                  </EuiBadge>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiBadge color="hollow">
                    {translate(
                      "anketa.opsanio.appliance.flyout.otobo.badge.closedCount",
                      {
                        values: { count: closedCount },
                        defaultMessage: "{{count}} closed",
                      },
                    )}
                  </EuiBadge>
                </EuiFlexItem>
              </>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        {canReply && queue && !queueMissing && !unavailable && view === "list" && (
          <EuiFlexItem grow={false}>
            <EuiButton
              onClick={() => setShowNewTicket((open) => !open)}
              size="s"
              fill={!showNewTicket}
              iconType="plusInCircle"
              disabled={loading}
            >
              {translate("anketa.opsanio.appliance.flyout.otobo.create.button", {
                defaultMessage: "New ticket",
              })}
            </EuiButton>
          </EuiFlexItem>
        )}
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={getTickets}
            size="s"
            iconType="refresh"
            isLoading={loading}
            disabled={loading}
          >
            {translate("anketa.opsanio.refresh", {
              defaultMessage: "Refresh",
            })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      {showNewTicket && canReply && queue && !queueMissing && view === "list" && (
        <>
          <EuiSpacer size="s" />
          <NewTicketForm
            instanceName={instanceName}
            queue={queue}
            onCancel={() => setShowNewTicket(false)}
            onCreated={() => {
              setShowNewTicket(false);
              void getTickets();
            }}
          />
        </>
      )}

      {queue && (
        <>
          <EuiSpacer size="xs" />
          <EuiText size="xs" color="subdued">
            <EuiIcon type="folderClosed" size="s" /> {queue}
          </EuiText>
        </>
      )}

      <EuiHorizontalRule margin="m" />

      {loading && (
        <EuiFlexGroup
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: 200 }}
        >
          <EuiFlexItem grow={false}>
            <EuiFlexGroup direction="column" alignItems="center" gutterSize="m">
              <EuiLoadingSpinner size="xl" />
              <EuiText size="s" color="subdued">
                {translate("anketa.opsanio.appliance.flyout.otobo.loading", {
                  defaultMessage: "Loading tickets…",
                })}
              </EuiText>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}

      {!loading && unavailable && (
        <EuiEmptyPrompt
          iconType="offline"
          title={
            <h3>
              {translate(
                "anketa.opsanio.appliance.flyout.otobo.unavailable.title",
                { defaultMessage: "OTOBO unavailable" },
              )}
            </h3>
          }
          body={
            <EuiText size="s" color="subdued">
              {unavailableMsg ??
                translate(
                  "anketa.opsanio.appliance.flyout.otobo.unavailable.body",
                  {
                    defaultMessage:
                      "The OTOBO connection is currently not configured.",
                  },
                )}
              <EuiSpacer size="s" />
              {translate(
                "anketa.opsanio.appliance.flyout.otobo.unavailable.hint",
                {
                  values: { queue: queue || instanceName },
                  defaultMessage:
                    "The queue {{queue}} and the mail flow can be set up manually via an email request.",
                },
              )}
            </EuiText>
          }
          actions={setupCta}
        />
      )}

      {!loading && !unavailable && error && (
        <EuiCallOut
          title={translate(
            "anketa.opsanio.appliance.flyout.otobo.error.loadTitle",
            { defaultMessage: "Error loading tickets" },
          )}
          color="danger"
          iconType="alert"
        >
          <p>{error}</p>
          <EuiButton size="s" color="danger" onClick={getTickets}>
            {translate("anketa.opsanio.appliance.flyout.otobo.error.retry", {
              defaultMessage: "Try again",
            })}
          </EuiButton>
        </EuiCallOut>
      )}

      {!loading && !unavailable && !error && queueMissing && (
        <EuiEmptyPrompt
          iconType="folderClosed"
          title={
            <h3>
              {translate(
                "anketa.opsanio.appliance.flyout.otobo.queueMissing.title",
                { defaultMessage: "Queue does not exist" },
              )}
            </h3>
          }
          body={
            <>
              <EuiText size="s" color="subdued">
                {translate(
                  "anketa.opsanio.appliance.flyout.otobo.queueMissing.body",
                  {
                    values: { queue },
                    defaultMessage:
                      "The OTOBO queue {{queue}} does not exist yet. Since the OTOBO interface provides no API for creating queues and PostMaster filters, the setup must be done manually. The button below triggers a setup request by email.",
                  },
                )}
              </EuiText>
              <EuiSpacer size="m" />
              <EuiPanel
                paddingSize="m"
                hasShadow={false}
                color="subdued"
                hasBorder
                style={{ textAlign: "left" }}
              >
                <EuiText
                  size="xs"
                  color="subdued"
                  style={{
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    fontWeight: 600,
                  }}
                >
                  {translate(
                    "anketa.opsanio.appliance.flyout.otobo.queueMissing.setupHeading",
                    { defaultMessage: "To be set up in OTOBO" },
                  )}
                </EuiText>
                <EuiSpacer size="s" />
                <EuiText size="s">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li>
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.subQueue.before",
                        { defaultMessage: "Create sub-queue" },
                      )}{" "}
                      <EuiCode>{queue}</EuiCode>{" "}
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.subQueue.after",
                        { defaultMessage: "below the master queue." },
                      )}
                    </li>
                    <li>
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.filter.lead",
                        {
                          defaultMessage:
                            "PostMaster filter per customer domain: match on",
                        },
                      )}{" "}
                      <em>To</em>{" "}
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.filter.toEquals",
                        { defaultMessage: "= ticket address and" },
                      )}{" "}
                      <em>From</em> ={" "}
                      <EuiCode>{"^[A-Za-z0-9._%+-]+@<domain>$"}</EuiCode>,{" "}
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.filter.setsHeader",
                        { defaultMessage: "sets header" },
                      )}{" "}
                      <EuiCode>X-OTRS-Queue</EuiCode> ={" "}
                      <EuiCode>{queue}</EuiCode>.
                    </li>
                    <li>
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.monitoring.before",
                        { defaultMessage: "Monitoring/PRTG: mails from" },
                      )}{" "}
                      <EuiCode>monitoring@medocino.net</EuiCode>{" "}
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.monitoring.middle",
                        {
                          defaultMessage:
                            "whose header contains the instance name (",
                        },
                      )}
                      <EuiCode>{`.*${instanceName}.*`}</EuiCode>
                      {translate(
                        "anketa.opsanio.appliance.flyout.otobo.queueMissing.step.monitoring.afterName",
                        { defaultMessage: "), route into the same queue" },
                      )}{" "}
                      <EuiCode>{queue}</EuiCode>.
                    </li>
                  </ul>
                </EuiText>
              </EuiPanel>
            </>
          }
          actions={queueMissingActions}
        />
      )}

      {!loading &&
        !unavailable &&
        !error &&
        !queueMissing &&
        tickets.length === 0 && (
          <EuiEmptyPrompt
            icon={<EuiIcon type="listAdd" size="xl" color="subdued" />}
            title={
              <h3>
                {translate(
                  "anketa.opsanio.appliance.flyout.otobo.empty.title",
                  { defaultMessage: "No tickets found" },
                )}
              </h3>
            }
            body={
              <EuiText size="s" color="subdued">
                {translate(
                  "anketa.opsanio.appliance.flyout.otobo.empty.body",
                  {
                    defaultMessage:
                      "There are currently no OTOBO tickets for this instance.",
                  },
                )}
              </EuiText>
            }
          />
        )}

      {!loading &&
        !unavailable &&
        !error &&
        !queueMissing &&
        tickets.length > 0 && (
          <>
            {view === "list" && (
              <>
                <EuiPanel
                  paddingSize="m"
                  hasShadow={false}
                  color="subdued"
                  hasBorder
                  style={{ marginBottom: 12 }}
                >
                  <EuiFlexGroup
                    gutterSize="m"
                    alignItems="center"
                    responsive={false}
                    wrap
                  >
                    <EuiFlexItem>
                      <EuiFieldSearch
                        placeholder={translate(
                          "anketa.opsanio.appliance.flyout.otobo.search.placeholder",
                          {
                            defaultMessage:
                              "Search tickets… (title, number, owner, customer)",
                          },
                        )}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        isClearable
                        compressed
                        fullWidth
                      />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiButtonGroup
                        legend={translate(
                          "anketa.opsanio.appliance.flyout.otobo.column.status",
                          { defaultMessage: "Status" },
                        )}
                        options={stateButtons}
                        idSelected={stateFilter}
                        onChange={(id: string) =>
                          setStateFilter(id as StateFilter)
                        }
                        buttonSize="compressed"
                        color="primary"
                      />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiText size="xs" color="subdued">
                        {filteredTickets.length === tickets.length
                          ? translate(
                              "anketa.opsanio.appliance.flyout.otobo.count.total",
                              {
                                values: { total: tickets.length },
                                defaultMessage: "{{total}} tickets",
                              },
                            )
                          : translate(
                              "anketa.opsanio.appliance.flyout.otobo.count.filtered",
                              {
                                values: {
                                  shown: filteredTickets.length,
                                  total: tickets.length,
                                },
                                defaultMessage:
                                  "{{shown}} of {{total}} tickets",
                              },
                            )}
                      </EuiText>
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiPanel>

                {filteredTickets.length === 0 ? (
                  <EuiEmptyPrompt
                    icon={<EuiIcon type="filter" size="xl" color="subdued" />}
                    title={
                      <h3>
                        {translate(
                          "anketa.opsanio.appliance.flyout.otobo.noMatches.title",
                          { defaultMessage: "No matches" },
                        )}
                      </h3>
                    }
                    body={
                      <EuiText size="s" color="subdued">
                        {translate(
                          "anketa.opsanio.appliance.flyout.otobo.noMatches.body",
                          {
                            defaultMessage:
                              "No tickets match the current filters.",
                          },
                        )}
                      </EuiText>
                    }
                  />
                ) : (
                  <EuiInMemoryTable
                    items={filteredTickets}
                    columns={columns}
                    sorting={{ sort: { field: "Changed", direction: "desc" } }}
                    pagination={{
                      initialPageSize: 10,
                      pageSizeOptions: [10, 25, 50],
                    }}
                    rowProps={(ticket: OtoboTicket) => ({
                      style: {
                        cursor: "pointer",
                        background:
                          selected?.TicketID === ticket.TicketID
                            ? "rgba(0,119,204,0.06)"
                            : undefined,
                      },
                      onClick: () => handleSelect(ticket),
                    })}
                  />
                )}
              </>
            )}

            {view === "detail" && selected && (
              <TicketDetailView
                ticket={selected}
                loadingDetail={loadingDetail}
                formatDate={formatDate}
                onBack={handleBack}
                translate={translate}
                canReply={canReply}
                onUpdated={setSelected}
              />
            )}
          </>
        )}
    </EuiPanel>
  );
};
